// src/game-system/races/dwarf.js
import { BaseRace } from './baseRace';

/**
 * Base traits shared by all dwarves
 * @type {Array<RacialTrait>}
 */
const BASE_DWARVEN_TRAITS = [
  {
    name: "Dwarven Resilience",
    description: "Your dwarven heritage grants you hardiness",
    effect: {
      type: "passive",
      mechanics: "Advantage on saving throws against poison, resistance to poison damage"
    }
  },
  {
    name: "Dwarven Combat Training",
    description: "You're trained with traditional dwarven weapons",
    effect: {
      type: "passive",
      mechanics: "Proficiency with battleaxe, handaxe, light hammer, and warhammer"
    }
  },
  {
    name: "Tool Proficiency",
    description: "You gain proficiency with artisan's tools",
    effect: {
      type: "passive",
      mechanics: "Proficiency with smith's tools, brewer's supplies, or mason's tools"
    }
  },
  {
    name: "Stonecunning",
    description: "Your connection to stone runs deep",
    effect: {
      type: "passive",
      mechanics: "Add double proficiency bonus to History checks related to stonework"
    }
  },
  {
    name: "Darkvision",
    description: "You can see in dark conditions",
    effect: {
      type: "passive",
      mechanics: "You can see in dim light within 60 feet as if it were bright light"
    }
  }
];

/**
 * Dwarven subrace definitions
 * @type {Object.<string, DwarvenSubrace>}
 */
const DWARVEN_SUBRACES = {
  hill: {
    name: "Hill Dwarf",
    description: "Tough and wise dwarves known for their healing abilities",
    attributeBonuses: {
      wisdom: 1
    },
    traits: [
      {
        name: "Dwarven Toughness",
        description: "Your hit points increase",
        effect: {
          type: "passive",
          mechanics: "Hit point maximum increases by 1, and again each time you gain a level"
        }
      }
    ]
  },
  mountain: {
    name: "Mountain Dwarf",
    description: "Strong dwarves trained in armor mastery",
    attributeBonuses: {
      strength: 2
    },
    traits: [
      {
        name: "Dwarven Armor Training",
        description: "You're trained to wear armor",
        effect: {
          type: "passive",
          mechanics: "Proficiency with light and medium armor"
        }
      }
    ]
  }
};

/**
 * Dwarven cultures
 * @type {Array<CulturalBackground>}
 */
const DWARVEN_CULTURES = [
  {
    name: "Mountain Hold",
    description: "Ancient strongholds carved into the mountains",
    bonusSkills: ["Athletics", "History"],
    startingEquipment: ["Smith's tools", "Mining pick", "Stone hammer"],
    traditions: [
      {
        name: "Master Craftsman",
        benefit: "Advantage on checks to appraise stonework or metalwork"
      }
    ]
  },
  {
    name: "Trading Clan",
    description: "Merchant clans who trade dwarven goods",
    bonusSkills: ["Persuasion", "Investigation"],
    startingEquipment: ["Trading ledger", "Fine clothes", "Merchant's scale"],
    traditions: [
      {
        name: "Eye for Value",
        benefit: "Advantage on checks to determine the value of precious metals and gems"
      }
    ]
  }
];

export class Dwarf extends BaseRace {
  /**
   * @param {string} subrace - Name of the dwarven subrace
   */
  constructor(subrace) {
    const subraceData = DWARVEN_SUBRACES[subrace];
    if (!subraceData) {
      throw new Error(`Invalid dwarf subrace: ${subrace}`);
    }

    const config = {
      name: subraceData.name,
      description: subraceData.description,
      attributeBonuses: {
        constitution: 2,
        ...subraceData.attributeBonuses
      },
      traits: [...BASE_DWARVEN_TRAITS, ...subraceData.traits],
      cultures: DWARVEN_CULTURES,
      size: {
        category: "Medium",
        height: { min: 120, max: 150 },
        weight: { min: 60, max: 100 }
      },
      speed: 25,
      languages: {
        known: ["Common", "Dwarvish"],
        bonus: 0
      }
    };

    super(config);
    this.subrace = subrace;
  }

  /**
   * Get subrace-specific features
   * @returns {DwarvenSubrace}
   */
  getSubraceFeatures() {
    return DWARVEN_SUBRACES[this.subrace];
  }
}